/* eslint-disable */
import DiffMatchPatch from 'diff-match-patch';
const dmp = new DiffMatchPatch();

/**
 * HTML差异对比方法
 * @param {string} oldData - 旧的HTML内容
 * @param {string} newData - 新的HTML内容
 * @returns {string} 带有红色差异标记的新HTML内容
 */
export function htmlDiff(oldData, newData) {
  if (!oldData && !newData) return '';
  if (!oldData) return newData;
  if (!newData) return '';

  try {
    // 提取HTML结构和文本内容
    const oldHtmlInfo = extractHtmlStructure(oldData);
    const newHtmlInfo = extractHtmlStructure(newData);

    // 对比文本内容
    const diffs = dmp.diff_main(oldHtmlInfo.text, newHtmlInfo.text);
    dmp.diff_cleanupSemantic(diffs);

    // 重新构建带有差异标记的新HTML
    return rebuildHtmlWithDiff(newHtmlInfo, diffs);
  } catch (error) {
    console.error('HTML差异对比出错:', error);
    return newData;
  }
}

/**
 * 提取HTML结构信息
 * @param {string} html - HTML内容
 * @returns {Object} 包含标签、文本和结构信息的对象
 */
function extractHtmlStructure(html) {
  const tagRegex = /<[^>]*>/g;
  const singleTagRegex = /<(img|br|hr|input|meta|link|area|base|col|embed|param|source|track|wbr|!DOCTYPE)[^>]*>/i;

  const result = {
    text: '',
    segments: [],
    tags: [],
    originalHtml: html
  };

  let lastIndex = 0;
  let match;
  let textIndex = 0;

  while ((match = tagRegex.exec(html)) !== null) {
    // 添加标签前的文本
    const textBefore = html.substring(lastIndex, match.index);
    if (textBefore) {
      result.text += textBefore;
      result.segments.push({
        type: 'text',
        content: textBefore,
        textStartIndex: textIndex,
        textEndIndex: textIndex + textBefore.length
      });
      textIndex += textBefore.length;
    }

    // 添加标签
    const tag = match[0];
    const isSingleTag = singleTagRegex.test(tag);

    result.segments.push({
      type: isSingleTag ? 'single-tag' : 'tag',
      content: tag,
      isClosingTag: tag.startsWith('</'),
      tagType: isSingleTag ? 'single' : (tag.startsWith('</') ? 'closing' : 'opening')
    });

    lastIndex = tagRegex.lastIndex;
  }

  // 添加最后的文本
  const remainingText = html.substring(lastIndex);
  if (remainingText) {
    result.text += remainingText;
    result.segments.push({
      type: 'text',
      content: remainingText,
      textStartIndex: textIndex,
      textEndIndex: textIndex + remainingText.length
    });
  }

  return result;
}

/**
 * 重新构建带有差异标记的HTML
 * @param {Object} newHtmlInfo - 新HTML的结构信息
 * @param {Array} diffs - 差异信息数组
 * @returns {string} 带有差异标记的HTML
 */
function rebuildHtmlWithDiff(newHtmlInfo, diffs) {
  let result = '';
  const currentTextIndex = 0;

  // 创建文本差异映射
  const textDiffMap = createTextDiffMap(diffs);

  // 遍历新HTML的所有片段
  for (const segment of newHtmlInfo.segments) {
    if (segment.type === 'text') {
      // 处理文本片段，应用差异标记
      const processedText = applyDiffToText(segment.content, segment.textStartIndex, textDiffMap);
      result += processedText;
    } else {
      // 直接添加标签
      result += segment.content;
    }
  }

  return result;
}

/**
 * 创建文本差异映射
 * @param {Array} diffs - 差异信息数组
 * @returns {Object} 差异映射对象
 */
function createTextDiffMap(diffs) {
  const diffMap = {};
  let currentIndex = 0;

  for (const [operation, text] of diffs) {
    if (text.length === 0) continue;

    const startIndex = currentIndex;
    const endIndex = currentIndex + text.length;

    for (let i = startIndex; i < endIndex; i++) {
      diffMap[i] = {
        operation,
        char: text[i - startIndex]
      };
    }

    currentIndex = endIndex;
  }

  return diffMap;
}

/**
 * 对文本应用差异标记
 * @param {string} text - 原始文本
 * @param {number} textStartIndex - 文本在整个HTML中的起始索引
 * @param {Object} textDiffMap - 差异映射
 * @returns {string} 带有差异标记的文本
 */
function applyDiffToText(text, textStartIndex, textDiffMap) {
  let result = '';
  let inDiff = false;
  let diffBuffer = '';

  for (let i = 0; i < text.length; i++) {
    const globalIndex = textStartIndex + i;
    const diffInfo = textDiffMap[globalIndex];

    if (diffInfo && diffInfo.operation === 1) { // INSERT operation
      // 开始或继续差异内容
      if (!inDiff) {
        // 如果有缓冲的内容，先输出
        // eslint-disable-next-line max-depth
        if (diffBuffer) {
          result += diffBuffer;
          diffBuffer = '';
        }
        inDiff = true;
        result += '<span style="color:red">';
      }
      result += diffInfo.char;
    } else {
      // 非差异内容
      if (inDiff) {
        result += '</span>';
        inDiff = false;
      }
      result += diffInfo ? diffInfo.char : (text[i] || '');
    }
  }

  // 关闭可能未关闭的差异标签
  if (inDiff) {
    result += '</span>';
  }

  return result;
}

/**
 * 简化的HTML差异对比方法（适用于简单场景）
 * @param {string} oldData - 旧的HTML内容
 * @param {string} newData - 新的HTML内容
 * @returns {string} 带有红色差异标记的新HTML内容
 */
export function simpleHtmlDiff(oldData, newData) {
  if (!newData) return '';

  try {
    // 使用更简单的文本提取方法
    const oldText = stripHtmlTags(oldData);
    const newText = stripHtmlTags(newData);

    // 计算差异
    const diffs = dmp.diff_main(oldText, newText);
    dmp.diff_cleanupSemantic(diffs);

    // 应用差异到新HTML
    return applyDiffsToNewHtml(newData, diffs);
  } catch (error) {
    console.error('简单HTML差异对比出错:', error);
    return newData;
  }
}

/**
 * 获取字符级别的差异映射
 * @param {Array} diffs - diff-match-patch返回的差异数组
 * @returns {Array} 字符级别的差异信息数组
 */
function getCharDiffs(diffs) {
  const charDiffs = [];
  let position = 0;

  for (const [operation, text] of diffs) {
    for (let i = 0; i < text.length; i++) {
      charDiffs.push({
        position: position++,
        operation: operation, // -1: 删除, 0: 相等, 1: 插入
        char: text[i]
      });
    }
  }

  return charDiffs;
}

/**
 * 重写的HTML差异对比方法，专门处理文本对比
 * @param {string} oldData - 旧的HTML内容
 * @param {string} newData - 新的HTML内容
 * @returns {string} 带有红色差异标记的新HTML内容
 */
export function compareContentDiff(oldData, newData) {
  if (!newData) return '';

  try {
    const oldText = stripHtmlTags(oldData);
    const newText = stripHtmlTags(newData);

    const diffs = dmp.diff_main(oldText, newText);
    dmp.diff_cleanupSemantic(diffs);

    // 找出所有插入的字符
    const insertedText = diffs
      .filter(([op]) => op === 1)
      .map(([, text]) => text)
      .join('');

    if (!insertedText) {
      return newData;
    }

    // 在新HTML文本中找到插入的内容并标记
    return markInsertedTextInHtml(newData, insertedText);
  } catch (error) {
    console.error('HTML文本差异对比出错:', error);
    return newData;
  }
}

/**
 * 改进的HTML差异对比方法 - 正确处理多个差异段
 * @param {string} oldData - 旧的HTML内容
 * @param {string} newData - 新的HTML内容
 * @returns {string} 带有红色差异标记的新HTML内容
 */
export function htmlDiffMultiple(oldData, newData) {
  if (!newData) return '';

  try {
    const oldText = stripHtmlTags(oldData);
    const newText = stripHtmlTags(newData);

    const diffs = dmp.diff_main(oldText, newText);
    dmp.diff_cleanupSemantic(diffs);

    // 使用更直接的方法重建HTML
    return rebuildHtmlFromDiffs(newData, diffs);
  } catch (error) {
    console.error('HTML多差异段对比出错:', error);
    return newData;
  }
}

/**
 * 从diff结果直接重建HTML
 * @param {string} newHtml - 新的HTML内容
 * @param {Array} diffs - diff数组
 * @returns {string} 处理后的HTML
 */
function rebuildHtmlFromDiffs(newHtml, diffs) {
  // 获取新HTML的结构
  const htmlStructure = extractHtmlSegments(newHtml);
  let textIndex = 0;
  let result = '';

  for (const segment of htmlStructure) {
    if (segment.type === 'text') {
      // 将文本段根据diff进行着色
      const coloredText = colorizeTextSegment(segment.content, textIndex, diffs);
      result += coloredText;
      textIndex += segment.content.length;
    } else {
      // 保持标签不变
      result += segment.content;
    }
  }

  return result;
}

/**
 * 提取HTML结构段
 * @param {string} html - HTML内容
 * @returns {Array} 结构段数组
 */
function extractHtmlSegments(html) {
  const segments = [];
  let currentText = '';
  let currentTag = '';
  let inTag = false;

  for (let i = 0; i < html.length; i++) {
    const char = html[i];

    if (char === '<') {
      if (currentText) {
        segments.push({ type: 'text', content: currentText });
        currentText = '';
      }
      inTag = true;
      currentTag = char;
    } else if (char === '>') {
      currentTag += char;
      segments.push({ type: 'tag', content: currentTag });
      currentTag = '';
      inTag = false;
    } else if (inTag) {
      currentTag += char;
    } else {
      currentText += char;
    }
  }

  if (currentText) {
    segments.push({ type: 'text', content: currentText });
  }

  return segments;
}

/**
 * 为文本段着色
 * @param {string} text - 文本内容
 * @param {number} startIndex - 在总文本中的起始位置
 * @param {Array} diffs - diff数组
 * @returns {string} 着色后的文本
 */
function colorizeTextSegment(text, startIndex, diffs) {
  let result = '';
  let currentTextPos = startIndex;
  let inSpan = false;

  // 获取diff映射
  const diffMap = createDiffPositionMap(diffs);

  for (let i = 0; i < text.length; i++) {
    const globalPos = currentTextPos + i;
    const diffInfo = diffMap[globalPos];

    if (diffInfo) {
      if (diffInfo.operation === 1) { // INSERT
        // eslint-disable-next-line max-depth
        if (!inSpan) {
          result += '<span style="color:red">';
          inSpan = true;
        }
      } else {
        // eslint-disable-next-line max-depth
        if (inSpan) {
          result += '</span>';
          inSpan = false;
        }
      }
    }

    result += text[i];
  }

  if (inSpan) {
    result += '</span>';
  }

  return result;
}

/**
 * 创建diff位置映射
 * @param {Array} diffs - diff数组
 * @returns {Object} 位置映射
 */
function createDiffPositionMap(diffs) {
  const map = {};
  let position = 0;

  for (const [operation, text] of diffs) {
    if (operation === -1) { // DELETE - 跳过删除操作，不创建映射
      continue;
    }

    const endIndex = position + text.length;

    for (let i = position; i < endIndex; i++) {
      map[i] = {
        operation: operation,
        char: text[i - position]
      };
    }

    position = endIndex;
  }

  return map;
}

/**
 * 将差异应用到HTML，支持多个差异段
 * @param {string} html - 原HTML
 * @param {Array} diffs - diff数组
 * @param {string} oldText - 旧纯文本
 * @param {string} newText - 新纯文本
 * @returns {string} 处理后的HTML
 */
// eslint-disable-next-line complexity
function applyMultipleDiffsToHtml(html, diffs, oldText, newText) {
  // 获取所有插入的文本片段（可能有多个）
  const insertedSegments = [];
  let currentInsert = '';

  for (const [operation, text] of diffs) {
    if (operation === 1) { // INSERT
      currentInsert += text;
    } else if (operation === 0 || operation === -1) { // EQUAL或DELETE
      if (currentInsert) {
        insertedSegments.push(currentInsert);
        currentInsert = '';
      }
    }
  }

  if (currentInsert) {
    insertedSegments.push(currentInsert);
  }

  if (insertedSegments.length === 0) {
    return html;
  }

  // 将所有插入段应用为红色标记
  let result = html;
  const markedTexts = new Set(); // 避免重复标记

  for (const insertText of insertedSegments) {
    if (insertText.trim() && !markedTexts.has(insertText)) {
      const tempResult = markTextInHtmlImproved(result, insertText);
      if (tempResult !== result) {
        markedTexts.add(insertText);
        result = tempResult;
      }
    }
  }

  return result;
}

/**
 * 改进的HTML文本标记方法
 * @param {string} html - HTML内容
 * @param {string} textToMark - 要标记的文本
 * @returns {string} 标记后的HTML
 */
function markTextInHtmlImproved(html, textToMark) {
  if (!textToMark || textToMark.trim() === '') return html;

  // 直接在HTML中查找并标记文本
  let plainText = stripHtmlTags(html);
  const positions = [];

  // 查找所有出现的位置
  let startIndex = plainText.indexOf(textToMark);
  while (startIndex !== -1) {
    positions.push(startIndex);
    startIndex = plainText.indexOf(textToMark, startIndex + 1);
  }

  if (positions.length === 0) {
    return html;
  }

  // 从后往前标记，避免位置偏移
  let result = html;
  for (let i = positions.length - 1; i >= 0; i--) {
    const textPosition = positions[i];
    const htmlPosition = getHtmlPositionFromTextPosition(html, textPosition);

    if (htmlPosition !== -1) {
      const before = result.substring(0, htmlPosition);
      const after = result.substring(htmlPosition + textToMark.length);
      result = before + `<span style="color:red">${textToMark}</span>` + after;

      // 重新计算位置（因为我们添加了span标签，需要调整后续位置）
      if (i > 0) {
        // 更新HTML内容，用于下次查找
        plainText = stripHtmlTags(result);
      }
    }
  }

  return result;
}

/**
 * 改进的在HTML文本中查找文本位置方法
 * @param {string} html - HTML内容
 * @param {string} searchText - 要查找的文本
 * @returns {number} 找到的位置，-1表示未找到
 */
function findTextInHtml(html, searchText) {
  const plainText = stripHtmlTags(html);
  const textIndex = plainText.indexOf(searchText);

  if (textIndex === -1) return -1;

  // 将纯文本位置转换为HTML位置
  return getHtmlPositionFromTextPosition(html, textIndex);
}

/**
 * 将纯文本位置转换为HTML位置的改进版本
 * @param {string} html - HTML内容
 * @param {number} textPosition - 纯文本位置
 * @returns {number} 对应的HTML位置
 */
function getHtmlPositionFromTextPosition(html, textPosition) {
  let textCount = 0;
  let inTag = false;

  for (let i = 0; i < html.length; i++) {
    const char = html[i];

    if (char === '<') {
      inTag = true;
    } else if (char === '>') {
      inTag = false;
      i++;
      continue;
    }

    if (inTag) {
      continue;
    }

    if (textCount === textPosition) {
      return i;
    }

    textCount++;
  }

  return -1;
}

/**
 * 在HTML中标记插入的文本
 * @param {string} html - HTML内容
 * @param {string} insertedText - 插入的文本
 * @returns {string} 带有标记的HTML
 */
function markInsertedTextInHtml(html, insertedText) {
  // 获取纯文本版本用于查找
  const plainText = stripHtmlTags(html);

  // 在纯文本中找到插入内容的位置
  const insertIndex = plainText.indexOf(insertedText);
  if (insertIndex === -1) {
    return html;
  }

  // 在HTML中找到对应的位置
  return markTextInHtmlByPosition(html, insertIndex, insertedText.length);
}

/**
 * 根据位置在HTML中标记文本
 * @param {string} html - HTML内容
 * @param {number} textIndex - 在纯文本中的起始位置
 * @param {number} length - 标记的文本长度
 * @returns {string} 带有标记的HTML
 */
function markTextInHtmlByPosition(html, textIndex, length) {
  const result = [];
  let htmlIndex = 0;
  let textCount = 0;
  let inTag = false;
  let marked = false;

  while (htmlIndex < html.length) {
    const char = html[htmlIndex];

    if (char === '<') {
      inTag = true;
    } else if (char === '>') {
      inTag = false;
      result.push(char);
      htmlIndex++;
      continue;
    }

    if (inTag) {
      result.push(char);
      htmlIndex++;
      continue;
    }

    // 这是一个文本字符
    if (textCount >= textIndex && textCount < textIndex + length && !marked) {
      result.push('<span style="color:red">');
      marked = true;
    }

    result.push(char);
    textCount++;

    if (textCount === textIndex + length && marked) {
      result.push('</span>');
      marked = false;
    }

    htmlIndex++;
  }

  return result.join('');
}

/**
 * 移除HTML标签，只保留文本 - 增强版，更好地处理空白字符
 * @param {string} html - HTML内容
 * @returns {string} 纯文本内容
 */
function stripHtmlTags(html) {
  if (!html) return '';

  return html
    .replace(/<!--[\s\S]*?-->/g, '') // 移除HTML注释
    .replace(/<!DOCTYPE[^>]*>/gi, '') // 移除DOCTYPE声明
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // 移除script标签
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // 移除style标签
    .replace(/<[^>]*>/g, '') // 移除所有HTML标签
    .replace(/&nbsp;/g, ' ') // 替换空格实体
    .replace(/&lt;/g, '<') // 替换小于号实体
    .replace(/&gt;/g, '>') // 替换大于号实体
    .replace(/&amp;/g, '&') // 替换&实体
    .replace(/&quot;/g, '"') // 替换引号实体
    .replace(/&#39;/g, "'") // 替换单引号实体
    .replace(/&#\d+;/g, '') // 移除数字实体引用
    .replace(/&[a-zA-Z]+;/g, '') // 移除命名实体引用
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // 移除零宽字符等
    .replace(/\s+/g, ' ') // 合并多个空白字符为一个空格
    .trim(); // 移除首尾空白
}

/**
 * 将差异应用到新HTML上
 * @param {string} newHtml - 新的HTML内容
 * @param {Array} diffs - 差异数组
 * @returns {string} 带有差异标记的HTML
 */
function applyDiffsToNewHtml(newHtml, diffs) {
  // 获取所有插入的字符
  const insertedChars = diffs
    .filter(([operation]) => operation === 1)
    .map(([, text]) => text)
    .join('');

  if (!insertedChars) return newHtml;

  // 在新HTML中找到插入的文本并标记为红色
  let result = newHtml;

  // 逐个字符查找并标记
  for (let i = 0; i < insertedChars.length; i++) {
    const char = insertedChars[i];
    const index = result.indexOf(char);
    if (index !== -1 && !isInTag(result, index)) {
      result = result.substring(0, index)
        + '<span style="color:red">' + char + '</span>'
        + result.substring(index + 1);
    }
  }

  return result;
}

/**
 * 检查指定位置是否在HTML标签内
 * @param {string} html - HTML内容
 * @param {number} index - 字符位置
 * @returns {boolean} 是否在标签内
 */
function isInTag(html, index) {
  let inTag = false;

  for (let i = 0; i < index; i++) {
    if (html[i] === '<') {
      inTag = true;
    } else if (html[i] === '>') {
      inTag = false;
    }
  }

  return inTag;
}

/**
 * 智能段落匹配的HTML差异对比方法
 * 适用于新旧数据段数不匹配的情况，如旧5段 vs 新3段
 * @param {string} oldData - 旧的HTML内容
 * @param {string} newData - 新的HTML内容
 * @returns {string} 带有红色差异标记的新HTML内容
 */
export function htmlDiffSmartMatching(oldData, newData) {
  if (!oldData && !newData) return '';
  if (!oldData) return newData;
  if (!newData) return '';

  try {
    // 提取段落
    const oldParagraphs = extractParagraphs(oldData);
    const newParagraphs = extractParagraphs(newData);

    // 如果段数相同，直接使用htmlDiffMultiple
    if (oldParagraphs.length === newParagraphs.length) {
      return htmlDiffMultiple(oldData, newData);
    }

    // 智能匹配段落并进行对比
    return applySmartParagraphMatching(oldParagraphs, newParagraphs);
  } catch (error) {
    console.error('智能段落对比出错:', error);
    return htmlDiffMultiple(oldData, newData); // 降级到原始方法
  }
}

/**
 * 从HTML中提取段落
 * @param {string} html - HTML内容
 * @returns {Array} 段落数组
 */
function extractParagraphs(html) {
  const paragraphs = [];
  const pRegex = /<p[^>]*>([\s\S]*?)<\/p>/gi;
  let match;

  while ((match = pRegex.exec(html)) !== null) {
    const textContent = stripHtmlTags(match[0]);
    paragraphs.push({
      fullHtml: match[0],
      textContent: textContent,
      startIndex: match.index,
      endIndex: match.index + match[0].length
    });
  }

  return paragraphs;
}

/**
 * 应用智能段落匹配
 * @param {Array} oldParagraphs - 旧段落数组
 * @param {Array} newParagraphs - 新段落数组
 * @returns {string} 处理后的HTML
 */
function applySmartParagraphMatching(oldParagraphs, newParagraphs) {
  // 创建匹配映射
  const matching = findBestParagraphMatches(oldParagraphs, newParagraphs);

  // 构建结果HTML
  let resultHtml = '';

  // 保留非段落内容
  const oldFullText = oldParagraphs.reduce((full, p) => full + p.fullHtml, '');
  const newFullText = newParagraphs.reduce((full, p) => full + p.fullHtml, '');

  // 对于每个匹配对，进行差异对比
  for (const match of matching) {
    if (match.oldIndex !== -1 && match.newIndex !== -1) {
      // 两个段落都存在，进行差异对比
      const oldPara = oldParagraphs[match.oldIndex];
      const newPara = newParagraphs[match.newIndex];
      const diffHtml = htmlDiffMultiple(oldPara.fullHtml, newPara.fullHtml);
      resultHtml += diffHtml;
    } else if (match.oldIndex === -1 && match.newIndex !== -1) {
      // 新段落，直接标记为新增
      const newPara = newParagraphs[match.newIndex];
      const MarkedNewPara = newPara.fullHtml.replace(
        /<p([^>]*)>/i,
        '<p$1><span style="color:red">'
      ).replace('</p>', '</span></p>');
      resultHtml += MarkedNewPara;
    }
    // 忽略删除的旧段落（不显示）
  }

  return resultHtml;
}

/**
 * 找到最佳的段落匹配
 * @param {Array} oldParagraphs - 旧段落
 * @param {Array} newParagraphs - 新段落
 * @returns {Array} 匹配结果数组
 */
function findBestParagraphMatches(oldParagraphs, newParagraphs) {
  const matches = [];
  const usedOld = new Set();
  const usedNew = new Set();

  // 首先尝试直接匹配（按顺序）
  const directMatches = Math.min(oldParagraphs.length, newParagraphs.length);
  for (let i = 0; i < directMatches; i++) {
    const oldPara = oldParagraphs[i];
    const newPara = newParagraphs[i];

    // 计算相似度
    const similarity = calculateTextSimilarity(oldPara.textContent, newPara.textContent);

    // 如果相似度超过阈值，认为它们是匹配的
    if (similarity > 0.3) { // 30%相似度阈值
      matches.push({ oldIndex: i, newIndex: i, similarity });
      usedOld.add(i);
      usedNew.add(i);
    }
  }

  // 处理未匹配的新段落（新增）
  for (let i = 0; i < newParagraphs.length; i++) {
    if (!usedNew.has(i)) {
      matches.push({ oldIndex: -1, newIndex: i, similarity: 0 });
    }
  }

  // 如果没有匹配到任何段落，使用端到端对比
  if (matches.length === 0 || !matches.some(m => m.oldIndex !== -1)) {
    return [{ oldIndex: -1, newIndex: 0, similarity: 0 }];
  }

  return matches.sort((a, b) => a.newIndex - b.newIndex);
}

/**
 * 计算文本相似度
 * @param {string} text1 - 文本1
 * @param {string} text2 - 文本2
 * @returns {number} 相似度 (0-1)
 */
function calculateTextSimilarity(text1, text2) {
  if (!text1 || !text2) return 0;

  const longer = text1.length > text2.length ? text1 : text2;
  const shorter = text1.length > text2.length ? text2 : text1;

  if (longer.length === 0) return 1.0;

  // 使用Levenshtein距离计算相似度
  const distance = levenshteinDistance(longer, shorter);
  return (longer.length - distance) / longer.length;
}

/**
 * 计算Levenshtein距离
 * @param {string} str1 - 字符串1
 * @param {string} str2 - 字符串2
 * @returns {number} 编辑距离
 */
function levenshteinDistance(str1, str2) {
  const matrix = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
}

/**
 * 简化的智能匹配方法 - 专门处理常见的段数不匹配情况
 * @param {string} oldData - 旧数据
 * @param {string} newData - 新数据
 * @returns {string} 处理结果
 */
export function htmlDiffParagraphBased(oldData, newData) {
  if (!oldData && !newData) return '';
  if (!oldData) return newData;
  if (!newData) return '';

  try {
    const oldParagraphs = extractParagraphs(oldData);
    const newParagraphs = extractParagraphs(newData);

    // 段数相同时直接对比
    if (oldParagraphs.length === newParagraphs.length) {
      return htmlDiffMultiple(oldData, newData);
    }

    let resultHtml = '';

    // 如果新段落数量 <= 旧段落数量，从中间开始匹配对应段落
    if (newParagraphs.length <= oldParagraphs.length && oldParagraphs.length > 0) {
      // 新数据从第1段开始对应旧数据的第2段，依此类推
      const startOffset = 1; // 偏移量，新段落从旧数据第2段开始匹配
      const maxOldIndex = oldParagraphs.length - 1; // 旧数据最大索引
      const maxNewIndex = newParagraphs.length - 1;   // 新数据最大索引

      for (let i = 0; i <= maxNewIndex; i++) {
        const newIndex = i;
        const oldIndex = startOffset + i;

        console.log(`匹配: 新段落${newIndex} -> 旧段落${oldIndex}`);
        console.log(`新内容: ${newParagraphs[newIndex].textContent}`);
        console.log(`旧内容: ${oldParagraphs[oldIndex]?.textContent || 'undefined'}`);

        if (oldIndex >= 0 && oldIndex < oldParagraphs.length) {
          const oldPara = oldParagraphs[oldIndex];
          const newPara = newParagraphs[newIndex];

          // 进行逐字符差异对比
          const oldText = stripHtmlTags(oldPara.fullHtml);
          const newText = stripHtmlTags(newPara.fullHtml);

          const diffs = dmp.diff_main(oldText, newText);
          dmp.diff_cleanupSemantic(diffs);

          // 如果差异很大，标记整个新段落
          const hasChanges = diffs.some(([op]) => op === 1);

          if (hasChanges) {
            // 将插入的部分标记为红色
            const coloredHtml = rebuildHtmlFromDiffs(newPara.fullHtml, diffs);
            resultHtml += coloredHtml;
          } else {
            resultHtml += newPara.fullHtml;
          }
        } else {
          // 新段落，标记为新增
          const newPara = newParagraphs[newIndex];
          const markedNewPara = newPara.fullHtml.replace(
            /<p([^>]*)>/i,
            '<p$1><span style="color:red">'
          ).replace('</p>', '</span></p>');
          resultHtml += markedNewPara;
        }
      }
    } else {
      // 新段落更多的情况，按顺序对比前面的，多余的标记为新增
      for (let i = 0; i < Math.min(oldParagraphs.length, newParagraphs.length); i++) {
        const diffHtml = htmlDiffMultiple(oldParagraphs[i].fullHtml, newParagraphs[i].fullHtml);
        resultHtml += diffHtml;
      }

      // 处理多余的新段落
      for (let i = oldParagraphs.length; i < newParagraphs.length; i++) {
        const newPara = newParagraphs[i];
        const markedNewPara = newPara.fullHtml.replace(
          /<p([^>]*)>/i,
          '<p$1><span style="color:red">'
        ).replace('</p>', '</span></p>');
        resultHtml += markedNewPara;
      }
    }

    return resultHtml;
  } catch (error) {
    console.error('段落对比出错:', error);
    return htmlDiffMultiple(oldData, newData); // 降级
  }
}

// 推荐使用 htmlDiffMultiple 方法处理多个差异段
// htmlDiff 工作原理：提取HTML结构和文本内容，对比文本内容，重新构建带有差异标记的新HTML

/**
 * 表格专用差异对比方法
 * 只标记表格内容差异，忽略表头差异
 * @param {string} oldData - 旧的HTML内容
 * @param {string} newData - 新的HTML内容
 * @returns {string} 带有红色差异标记的新HTML内容
 */
export function htmlDiffTableOnly(oldData, newData) {
  if (!oldData && !newData) return '';
  if (!oldData) return newData;
  if (!newData) return '';

  try {
    // 分离表头和表体
    const { oldHeader, oldBody, newHeader, newBody } = extractTableParts(oldData, newData);

    // 表头保持新版本，不标记差异
    let resultHtml = newHeader || '';

    // 如果有表体，只对表体进行差异对比
    if (oldBody && newBody) {
      const bodyDiff = htmlDiffMultiple(oldBody, newBody);
      resultHtml += bodyDiff;
    } else if (newBody) {
      // 如果只有新表体，直接添加
      resultHtml += newBody;
    }

    // 确保有表格标签
    return wrapWithTableIfNeeded(resultHtml, newData);
  } catch (error) {
    console.error('表格对比出错:', error);
    return htmlDiffMultiple(oldData, newData); // 降级到通用方法
  }
}

/**
 * 从HTML中提取表头和表体
 * @param {string} oldHtml - 旧HTML
 * @param {string} newHtml - 新HTML
 * @returns {Object} 分离后的表头和表体
 */
function extractTableParts(oldHtml, newHtml) {
  const result = {
    oldHeader: '',
    oldBody: '',
    newHeader: '',
    newBody: ''
  };

  // 提取表头 <thead>...</thead>
  const theadRegexOld = /<thead[^>]*>([\s\S]*?)<\/thead>/i;
  const theadRegexNew = /<thead[^>]*>([\s\S]*?)<\/thead>/i;

  const theadMatchOld = theadRegexOld.exec(oldHtml);
  const theadMatchNew = theadRegexNew.exec(newHtml);

  result.oldHeader = theadMatchOld ? theadMatchOld[0] : '';
  result.newHeader = theadMatchNew ? theadMatchNew[0] : '';

  // 提取表体 <tbody>...</tbody>
  const tbodyRegexOld = /<tbody[^>]*>([\s\S]*?)<\/tbody>/i;
  const tbodyRegexNew = /<tbody[^>]*>([\s\S]*?)<\/tbody>/i;

  const tbodyMatchOld = tbodyRegexOld.exec(oldHtml);
  const tbodyMatchNew = tbodyRegexNew.exec(newHtml);

  result.oldBody = tbodyMatchOld ? tbodyMatchOld[0] : extractTableContent(oldHtml, 'tbody');
  result.newBody = tbodyMatchNew ? tbodyMatchNew[0] : extractTableContent(newHtml, 'tbody');

  return result;
}

/**
 * 从表格中提取内容（如果没有thead/tbody标签）
 * @param {string} html - HTML内容
 * @param {string} part - 要提取的部分（'thead'或'tbody'）
 * @returns {string} 提取的内容
 */
function extractTableContent(html, part) {
  const tableContentRegex = /<table[^>]*>([\s\S]*?)<\/table>/i;
  const tableMatch = tableContentRegex.exec(html);

  if (!tableMatch) return html;

  const innerContent = tableMatch[1];

  if (part === 'thead') {
    // 查找第一个tr作为表头（简化处理）
    const firstRowRegex = /<tr[^>]*>([\s\S]*?)<\/tr>/;
    const firstRowMatch = firstRowRegex.exec(innerContent);
    return firstRowMatch ? `<thead>${firstRowMatch[0]}</thead>` : '';
  } else {
    // 提取除第一行外的内容作为表体
    const rows = innerContent.match(/<tr[^>]*>([\s\S]*?)<\/tr>/gi) || [];
    if (rows.length <= 1) return '';

    const bodyRows = rows.slice(1).join('');
    return `<tbody>${bodyRows}</tbody>`;
  }
}

/**
 * 确保结果被表格标签包裹
 * @param {string} content - 内容
 * @param {string} originalHtml - 原始HTML（用于获取表格属性）
 * @returns {string} 包裹后的HTML
 */
function wrapWithTableIfNeeded(content, originalHtml) {
  // 如果内容已经是完整的表格，直接返回
  if (content.trim().startsWith('<table')) {
    return content;
  }

  // 提取原表格的属性
  const tableMatch = originalHtml.match(/<table([^>]*)>/i);
  const tableAttrs = tableMatch ? ' ' + tableMatch[1] : '';

  return `<table${tableAttrs}>${content}</table>`;
}

/**
 * 智能表格差异对比方法
 * 自动检测是否为表格内容，如果是则使用表格专用方法
 * @param {string} oldData - 旧HTML内容
 * @param {string} newData - 新HTML内容
 * @param {Object} options - 选项
 * @returns {string} 带有红色差异标记的新HTML内容
 */
export function htmlDiffSmart(oldData, newData, options = {}) {
  const { ignoreHeader = false, preferTable = false } = options;

  // 检测是否包含表格
  const isTable1 = /<table|<tbody|<thead/i.test(oldData);
  const isTable2 = /<table|<tbody|<thead/i.test(newData);

  // 如果是表格内容且设置了忽略表头选项
  if ((isTable1 || isTable2) && ignoreHeader) {
    return htmlDiffTableOnly(oldData, newData);
  }

  // 如果明确指定使用表格方法
  if ((isTable1 || isTable2) && preferTable) {
    return htmlDiffTableOnly(oldData, newData);
  }

  // 默认使用通用方法
  return htmlDiffMultiple(oldData, newData);
}

/**
 * 混合HTML内容对比方法
 * 按顺序和类型自动配对对比HTML元素（p、table、div等）
 * @param {string} oldData - 旧的HTML内容
 * @param {string} newData - 新的HTML内容
 * @param {Object} options - 选项配置
 * @returns {string} 带有红色差异标记的新HTML内容
 */
export function htmlDiffMixedContent(oldData, newData, options = {}) {
  if (!oldData && !newData) return '';
  if (!oldData) return newData;
  if (!newData) return '';

  try {
    const {
      ignoreTableHeader = true,  // 表格是否忽略表头差异
      preferOriginalOrder = false  // 是否优先使用原始顺序还是类型匹配顺序
    } = options;

    // 提取所有HTML元素并按顺序分类
    const oldElements = extractHtmlElements(oldData);
    const newElements = extractHtmlElements(newData);

    // 按类型进行智能配对
    const pairings = createSmartPairings(oldElements, newElements, preferOriginalOrder);

    // 对每个配对进行差异对比
    let resultHtml = '';
    for (const pairing of pairings) {
      if (pairing.type === 'removed') {
        // 跳过删除的元素（不显示）
        continue;
      } else if (pairing.type === 'added') {
        // 新增的元素，标记为全部红色
        const markedElement = markAsNewContent(pairing.newElement.html);
        resultHtml += markedElement;
      } else if (pairing.type === 'matched') {
        // 配对的元素进行差异对比
        const diffResult = compareElementsByType(
          pairing.oldElement,
          pairing.newElement,
          { ignoreTableHeader }
        );
        resultHtml += diffResult;
      }
    }

    return resultHtml;
  } catch (error) {
    console.error('混合内容对比出错:', error);
    // 降级到通用方法
    return htmlDiffMultiple(oldData, newData);
  }
}

/**
 * 提取HTML元素并分类
 * @param {string} html - HTML内容
 * @returns {Array} 元素数组
 */
function extractHtmlElements(html) {
  const elements = [];
  const elementRegex = /<(\w+)[^>]*>([\s\S]*?)<\/\1>/gi;
  let match;

  while ((match = elementRegex.exec(html)) !== null) {
    const tagName = match[1].toLowerCase();
    const fullHtml = match[0];
    const innerContent = match[2];

    elements.push({
      tag: tagName,
      html: fullHtml,
      inner: innerContent,
      isTable: tagName === 'table',
      originalIndex: elements.length
    });
  }

  return elements;
}

/**
 * 智能配对算法 - 增强版，支持内容相似度匹配
 * @param {Array} oldElements - 旧元素数组
 * @param {Array} newElements - 新元素数组
 * @param {boolean} preferOriginalOrder - 是否优先使用原始顺序
 * @returns {Array} 配对结果
 */
function createSmartPairings(oldElements, newElements, preferOriginalOrder = false) {
  const pairings = [];
  const usedOld = new Set();
  const usedNew = new Set();

  if (preferOriginalOrder) {
    // 按原始顺序配对
    const maxPairs = Math.min(oldElements.length, newElements.length);
    for (let i = 0; i < maxPairs; i++) {
      pairings.push({
        type: 'matched',
        oldElement: oldElements[i],
        newElement: newElements[i]
      });
      usedOld.add(i);
      usedNew.add(i);
    }
  } else {
    // 智能配对 - 先按类型分组，再基于内容相似度匹配
    const typeGroupsNew = groupElementsByType(newElements);
    const typeGroupsOld = groupElementsByType(oldElements);

    // 对每种类型进行智能配对
    for (const [tagName, newIndices] of Object.entries(typeGroupsNew)) {
      const oldIndices = typeGroupsOld[tagName] || [];

      if (oldIndices.length === 0 || newIndices.length === 0) {
        // 没有对应的旧元素，都标记为新增
        for (const newIndex of newIndices) {
          pairings.push({
            type: 'added',
            newElement: newElements[newIndex]
          });
          usedNew.add(newIndex);
        }
        continue;
      }

      // 使用内容相似度进行最优配对
      const matches = createOptimalMatches(oldElements, oldIndices, newElements, newIndices);

      for (const match of matches) {
        if (match.type === 'matched') {
          pairings.push({
            type: 'matched',
            oldElement: match.oldElement,
            newElement: match.newElement,
            similarity: match.similarity
          });
          usedOld.add(match.oldIndex);
          usedNew.add(match.newIndex);
        } else if (match.type === 'added') {
          pairings.push({
            type: 'added',
            newElement: match.newElement
          });
          usedNew.add(match.newIndex);
        }
      }
    }
  }

  // 处理未使用的新元素
  for (let i = 0; i < newElements.length; i++) {
    if (!usedNew.has(i)) {
      pairings.push({
        type: 'added',
        newElement: newElements[i]
      });
    }
  }

  // 按新元素的顺序重新排序配对结果
  return pairings.sort((a, b) => {
    const aIndex = a.newElement ? a.newElement.originalIndex : 999999;
    const bIndex = b.newElement ? b.newElement.originalIndex : 999999;
    return aIndex - bIndex;
  });
}

/**
 * 创建最优匹配 - 基于内容相似度
 * @param {Array} oldElements - 旧元素数组
 * @param {Array} oldIndices - 旧元素索引数组
 * @param {Array} newElements - 新元素数组
 * @param {Array} newIndices - 新元素索引数组
 * @returns {Array} 最优匹配结果
 */
function createOptimalMatches(oldElements, oldIndices, newElements, newIndices) {
  const matches = [];
  const usedOld = new Set();
  const usedNew = new Set();

  // 计算所有可能的配对相似度
  const similarityMatrix = [];
  for (let oldIdx = 0; oldIdx < oldIndices.length; oldIdx++) {
    similarityMatrix[oldIdx] = [];
    for (let newIdx = 0; newIdx < newIndices.length; newIdx++) {
      const oldElement = oldElements[oldIndices[oldIdx]];
      const newElement = newElements[newIndices[newIdx]];
      const similarity = calculateElementSimilarity(oldElement, newElement);

      similarityMatrix[oldIdx][newIdx] = {
        similarity: similarity,
        oldIndex: oldIndices[oldIdx],
        newIndex: newIndices[newIdx],
        oldElement: oldElement,
        newElement: newElement
      };
    }
  }

  // 使用贪心算法找到最优配对
  if (oldIndices.length <= newIndices.length) {
    // 旧元素较少，为每个旧元素找到最相似的新元素
    for (let oldIdx = 0; oldIdx < oldIndices.length; oldIdx++) {
      let bestMatch = null;
      let bestNewIdx = -1;
      let maxSimilarity = 0;

      for (let newIdx = 0; newIdx < newIndices.length; newIdx++) {
        if (!usedNew.has(newIndices[newIdx])) {
          const sim = similarityMatrix[oldIdx][newIdx].similarity;
          if (sim > maxSimilarity) {
            maxSimilarity = sim;
            bestMatch = similarityMatrix[oldIdx][newIdx];
            bestNewIdx = newIndices[newIdx];
          }
        }
      }

      const SIMILARITY_THRESHOLD = 0.5; // 提高到50%相似度阈值，减少错误匹配

      if (bestMatch && maxSimilarity >= SIMILARITY_THRESHOLD) {
        matches.push({
          type: 'matched',
          oldIndex: oldIndices[oldIdx],
          newIndex: bestNewIdx,
          oldElement: bestMatch.oldElement,
          newElement: bestMatch.newElement,
          similarity: maxSimilarity
        });
        usedOld.add(oldIndices[oldIdx]);
        usedNew.add(bestNewIdx);
      }
    }

    // 处理未配对的新元素
    for (const newIdx of newIndices) {
      if (!usedNew.has(newIdx)) {
        matches.push({
          type: 'added',
          newIndex: newIdx,
          newElement: newElements[newIdx]
        });
      }
    }
  } else {
    // 新元素较少，为每个新元素找到最相似的旧元素
    for (let newIdx = 0; newIdx < newIndices.length; newIdx++) {
      let bestMatch = null;
      let bestOldIdx = -1;
      let maxSimilarity = 0;

      for (let oldIdx = 0; oldIdx < oldIndices.length; oldIdx++) {
        if (!usedOld.has(oldIndices[oldIdx])) {
          const sim = similarityMatrix[oldIdx][newIdx].similarity;
          if (sim > maxSimilarity) {
            maxSimilarity = sim;
            bestMatch = similarityMatrix[oldIdx][newIdx];
            bestOldIdx = oldIndices[oldIdx];
          }
        }
      }

      const SIMILARITY_THRESHOLD = 0.5; // 提高到50%相似度阈值

      if (bestMatch && maxSimilarity >= SIMILARITY_THRESHOLD) {
        matches.push({
          type: 'matched',
          oldIndex: bestOldIdx,
          newIndex: newIndices[newIdx],
          oldElement: bestMatch.oldElement,
          newElement: bestMatch.newElement,
          similarity: maxSimilarity
        });
        usedOld.add(bestOldIdx);
        usedNew.add(newIndices[newIdx]);
      } else {
        // 没有找到相似的旧元素，标记为新增
        matches.push({
          type: 'added',
          newIndex: newIndices[newIdx],
          newElement: newElements[newIndices[newIdx]]
        });
      }
    }
  }

  return matches;
}

/**
 * 计算两个元素的相似度
 * @param {Object} oldElement - 旧元素
 * @param {Object} newElement - 新元素
 * @returns {number} 相似度 (0-1)
 */
function calculateElementSimilarity(oldElement, newElement) {
  if (oldElement.tag !== newElement.tag) {
    return 0; // 不同类型，相似度为0
  }

  if (oldElement.isTable && newElement.isTable) {
    // 表格相似度计算
    return calculateTableSimilarity(oldElement.inner, newElement.inner);
  } else {
    // 普通元素相似度计算
    const oldText = stripHtmlTags(oldElement.html);
    const newText = stripHtmlTags(newElement.html);
    return calculateTextSimilarity(oldText, newText);
  }
}

/**
 * 计算表格相似度
 * @param {string} oldTableHtml - 旧表格HTML
 * @param {string} newTableHtml - 新表格HTML
 * @returns {number} 表格相似度 (0-1)
 */
function calculateTableSimilarity(oldTableHtml, newTableHtml) {
  // 提取表头和表格结构
  const oldStructure = extractTableStructure(oldTableHtml);
  const newStructure = extractTableStructure(newTableHtml);

  let similarity = 0;
  let factors = 0;

  // 1. 表头相似度
  if (oldStructure.headers.length === newStructure.headers.length && oldStructure.headers.length > 0) {
    const headerSimilarity = calculateHeaderTextSimilarity(oldStructure.headers, newStructure.headers);
    similarity += headerSimilarity * 0.4; // 表头占40%权重
    factors += 0.4;
  }

  // 2. 表头字段数相似度
  const headerCountSim = 1 - Math.abs(oldStructure.headers.length - newStructure.headers.length) / Math.max(oldStructure.headers.length, newStructure.headers.length);
  similarity += headerCountSim * 0.2; // 表头字段数占20%权重
  factors += 0.2;

  // 3. 行数相似度
  const rowSim = oldStructure.rows.length === 0 && newStructure.rows.length === 0 ? 1 :
    1 - Math.abs(oldStructure.rows.length - newStructure.rows.length) / Math.max(oldStructure.rows.length, newStructure.rows.length, 1);
  similarity += rowSim * 0.2; // 行数占20%权重
  factors += 0.2;

  // 4. 列数相似度
  const colSim = oldStructure.cols.length === 0 && newStructure.cols.length === 0 ? 1 :
    1 - Math.abs(oldStructure.cols.length - newStructure.cols.length) / Math.max(oldStructure.cols.length, newStructure.cols.length, 1);
  similarity += colSim * 0.2; // 列数占20%权重
  factors += 0.2;

  return factors > 0 ? similarity / factors : 0;
}

/**
 * 提取表格结构
 * @param {string} tableHtml - 表格HTML
 * @returns {Object} 表格结构信息
 */
function extractTableStructure(tableHtml) {
  const structure = {
    headers: [],
    rows: [],
    cols: []
  };

  // 提取表头
  const theadMatch = tableHtml.match(/<thead[^>]*>([\s\S]*?)<\/thead>/i);
  if (theadMatch) {
    const headerMatches = theadMatch[1].match(/<th[^>]*>([\s\S]*?)<\/th>/gi) || [];
    structure.headers = headerMatches.map(th => stripHtmlTags(th).trim());
  }

  // 提取行数
  const rowMatches = tableHtml.match(/<tr[^>]*>([\s\S]*?)<\/tr>/gi) || [];
  structure.rows = rowMatches;

  // 提取列数（取第一行的列数作为参考）
  if (rowMatches.length > 0) {
    const firstRowsHtml = rowMatches[0];
    const colMatches = firstRowsHtml.match(/<td[^>]*>|<th[^>]*>/gi) || [];
    structure.cols = colMatches;
  }

  return structure;
}

/**
 * 计算表头文本相似度
 * @param {Array} oldHeaders - 旧表头数组
 * @param {Array} newHeaders - 新表头数组
 * @returns {number} 表头相似度
 */
function calculateHeaderTextSimilarity(oldHeaders, newHeaders) {
  if (oldHeaders.length === 0 && newHeaders.length === 0) return 1;
  if (oldHeaders.length === 0 || newHeaders.length === 0) return 0;

  let matches = 0;
  for (let i = 0; i < Math.min(oldHeaders.length, newHeaders.length); i++) {
    const sim = calculateTextSimilarity(oldHeaders[i], newHeaders[i]);
    if (sim > 0.7) matches++; // 70%以上相似度认为匹配
  }

  // 考虑位置变化的匹配
  for (let i = 0; i < newHeaders.length; i++) {
    for (let j = 0; j < oldHeaders.length; j++) {
      const sim = calculateTextSimilarity(newHeaders[i], oldHeaders[j]);
      if (sim > 0.8) { // 80%以上相似度认为匹配
        matches += 0.5;
        break;
      }
    }
  }

  return matches / Math.max(oldHeaders.length, newHeaders.length);
}

/**
 * 按元素类型分组
 * @param {Array} elements - 元素数组
 * @returns {Object} 按类型分组的索引
 */
function groupElementsByType(elements) {
  const groups = {};
  elements.forEach((element, index) => {
    const tag = element.tag;
    if (!groups[tag]) {
      groups[tag] = [];
    }
    groups[tag].push(index);
  });
  return groups;
}

/**
 * 按元素类型进行差异对比
 * @param {Object} oldElement - 旧元素
 * @param {Object} newElement - 新元素
 * @param {Object} options - 选项
 * @returns {string} 差异HTML
 */
function compareElementsByType(oldElement, newElement, options) {
  const { ignoreTableHeader = true } = options;

  if (oldElement.isTable && newElement.isTable) {
    // 表格对比
    if (ignoreTableHeader) {
      return htmlDiffTableOnly(oldElement.html, newElement.html);
    } else {
      return htmlDiffMultiple(oldElement.html, newElement.html);
    }
  } else if (oldElement.tag === newElement.tag) {
    // 相同标签类型的内容对比
    return htmlDiffMultiple(oldElement.html, newElement.html);
  } else {
    // 不同标签类型，直接显示新元素
    return newElement.html;
  }
}

/**
 * 标记为新增内容
 * @param {string} html - HTML内容
 * @returns {string} 标记后的HTML
 */
function markAsNewContent(html) {
  // 整体标记为新增内容
  const tagMatch = html.match(/<(\w+)([^>]*)>/i);
  if (!tagMatch) return html;

  const tagName = tagMatch[1];
  const attrs = tagMatch[2];
  const content = html.replace(/<[^>]*>/i, '').replace(/<\/[^>]*>/i, '');

  return `<${tagName}${attrs}><span style="color:red">${content}</span></${tagName}>`;
}

// 默认导出推荐的方法
export default htmlDiffMixedContent;
